// Napojení na Supabase (db, přihlášení, úložiště, stahování) — viz CLAUDE.md.
// Načítá se před src/app.js (pořadí importů v src/main.js).
// ===================== Supabase napojení (náhrada za Claude platformu) =====================
// Tahle vrstva beze zbytku nahrazuje window.claude.use(...), které appka původně
// dostávala od Claude platformy. Zbytek appky (dole) zůstává nezměněný — volá
// přesně stejné claude.use('db'/'user'/'assets'/'downloads') jako dřív, jen teď
// mluví se Supabase databází/úložištěm/přihlašováním místo s Claude.
import { idb, isNetworkError, isOffline, withTimeout, outbox, applyItem } from './offline.js';
import { AUTH_KEY, authStorage, cookieStorage, migrateLocalSession } from './core/auth-cookie.js';
import { captchaToken, captchaErrorText, prefetchCaptcha } from './core/captcha.js';
(function(){
  "use strict";

  var SUPABASE_URL = "https://irsnrptiazbnnvogjqsv.supabase.co";
  var SUPABASE_KEY = "sb_publishable_mZjpCg-g-EWLMJjUIivywA_vHdSdT5K";
  // Data Auditu jsou ve vlastním schématu `audit` (krok 3 portálu, 2026-11-01)
  // — platí pro .from(), .rpc() i realtime níže.
  var DB_SCHEMA = 'audit';
  // Sdílené přihlášení s webem a portálem (krok 4d, 2026-11-02): na
  // *.bozp-software.cz je relace v cookie pro celou doménu (core/auth-cookie.js).
  migrateLocalSession();
  var sb = window.supabase.createClient(SUPABASE_URL, SUPABASE_KEY, {db: {schema: DB_SCHEMA}, auth: {storage: authStorage()}});
  // Adresa portálu BOZP-SOFTWARE (zkušební verze, licence, platby) — testovací
  // Audit (dev.audit…, netlify.app) → testovací portál.
  var PORTAL_URL = (location.hostname === 'audit.bozp-software.cz' || location.hostname === 'audit.bozp-software.online')
    ? 'https://bozp-software.cz' : 'https://dev.bozp-software.cz';

  // Firestore-styl názvy sbírek (camelCase, jak je používá appka) -> skutečné
  // názvy tabulek v Postgres (snake_case).
  var TABLE = {
    workplaces:'workplaces', branches:'branches', companies:'companies',
    auditTypes:'audit_types', groups:'groups', questions:'questions',
    subquestions:'subquestions', roles:'roles', profiles:'profiles',
    appSettings:'app_settings', correctiveActions:'corrective_actions', audits:'audits',
    responsiblePersons:'responsible_persons', schedules:'audit_schedules',
    auditorGroups:'auditor_groups', delegations:'schedule_delegations', feedback:'feedback'
  };
  function tableFor(name){ return TABLE[name] || name; }

  // ---------- přihlášení ----------
  var currentUser = null;
  var myOrgId = null;
  var authResolved = false;
  var authWaiters = [];
  function resolveAuth(user){
    currentUser = user;
    // Every row belongs to an "org" (identified by the id of the
    // administrator who owns that data) — look up which org this person
    // belongs to before letting anything else in the app read or write,
    // since every write gets stamped with this value (see dbCollection/dbDoc
    // below). If there's no role row yet, fall back to treating them as the
    // owner of their own org — that only matters for the very first
    // bootstrap of a brand-new independent administrator.
    // Bez signálu: organizace z minulého přihlášení (localStorage).
    var orgKey = 'bozp-org-' + user.id;
    withTimeout(Promise.resolve(sb.from('roles').select('data').eq('id', user.id).maybeSingle()), 10000).then(function(res){
      var d = (res.data && res.data.data) || {};
      if(res.error){ try{ var cached = localStorage.getItem(orgKey); if(cached) d = {orgId: cached}; }catch(e){} }
      // Organizace zakládá jen portál: bez role = bez přístupu; pozastavený
      // uživatel (nad počet licencí) se nepřihlásí.
      // Bez role: nejdřív ověřit relaci na serveru — prošlé/zrušené přihlášení
      // (např. odhlášení na jiném zařízení) není „účet bez organizace“, ale
      // odhlášení → přihlašovací okno (dřív zůstalo viset „Nemáte přístup“).
      if(!res.error && !d.orgId){
        sb.auth.getUser().then(function(u){
          if(u && u.data && u.data.user && !u.error) showAccessOverlay('none');
          else { window.__sbAuth.clearLocalSession(); currentUser = null; showLoginOrPublicCard(); }
        }, function(){ showAccessOverlay('none'); });
        return;
      }
      if(!res.error && d.blokovan){ showAccessOverlay('paused'); return; }
      myOrgId = d.orgId || user.id;
      if(!res.error){ try{ localStorage.setItem(orgKey, myOrgId); }catch(e){} }
      startSync();
      loadLicence();
      authResolved = true;
      var w = authWaiters; authWaiters = [];
      w.forEach(function(fn){ fn(); });
    });
  }
  // Bez přístupu do Auditu (žádná role) nebo pozastavený uživatel.
  function showAccessOverlay(kind){
    if(document.getElementById('sbAccessOverlay')) return;
    var overlay = document.createElement('div');
    overlay.id = 'sbAccessOverlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:#11161dcc;display:flex;align-items:center;justify-content:center;z-index:99999;font-family:\'IBM Plex Sans\',system-ui,-apple-system,sans-serif;padding:16px;';
    var title = kind === 'paused' ? 'Přístup je pozastavený' : 'Nemáte přístup do aplikace';
    var text = kind === 'paused'
      ? 'Administrátor vaší organizace pozastavil váš přístup (počet licencí). Obraťte se na něj.'
      : 'Tento účet zatím nepatří do žádné organizace. Vyzkoušejte aplikaci zdarma na 30 dní, nebo požádejte administrátora své organizace o pozvánku.';
    overlay.innerHTML =
      '<div style="background:#fff;padding:28px 24px;border-radius:14px;width:340px;max-width:100%;box-shadow:0 10px 40px rgba(0,0,0,.35);text-align:center">'+
        APP_LOGO_LIGHT_SVG+
        '<div style="font-weight:700;font-size:var(--fs-2xl);margin-bottom:6px;color:#1a2230">'+title+'</div>'+
        '<div style="font-size:var(--fs-md);color:#5b6673;margin-bottom:10px;line-height:1.45">'+text+'</div>'+
        (currentUser && currentUser.email ? '<div style="font-size:var(--fs-base);color:#5b6673;margin-bottom:16px">Přihlášen jako <b style="color:#1a2230">'+String(currentUser.email).replace(/</g,'&lt;')+'</b></div>' : '')+
        (kind === 'paused' ? '' : '<a href="'+PORTAL_URL+'" style="display:block;padding:12px;background:#1f4e8c;color:#fff;border-radius:8px;font-size:var(--fs-lg);font-weight:600;text-decoration:none;margin-bottom:8px">Vyzkoušet zdarma</a>')+
        '<button type="button" id="sbAccessLogout" style="width:100%;padding:10px;background:none;color:#1f4e8c;border:none;font-size:var(--fs-md);font-weight:600;cursor:pointer">Odhlásit se a přihlásit jiným účtem</button>'+
      '</div>';
    document.body.appendChild(overlay);
    // Odhlásit vždy jen v tomto zařízení a uloženou relaci smazat i ručně —
    // signOut() u už zrušené relace na serveru nic neudělá (Session not found).
    overlay.querySelector('#sbAccessLogout').addEventListener('click', function(){
      var done = function(){ window.__sbAuth.clearLocalSession(); location.reload(); };
      Promise.resolve(sb.auth.signOut({scope:'local'})).then(done, done);
      setTimeout(done, 3000);
    });
  }
  // ---------- licence (portál → audit.licence) ----------
  // window.__licence.get() → {stav, pocet, plati_do} | null (bez řádku = bez omezení
  // v aplikaci, rozhoduje databáze). Změnu oznamuje událost bozp-licence.
  var licence = null;
  function setLicence(l){
    licence = l || null;
    try{ localStorage.setItem('bozp-licence-' + myOrgId, JSON.stringify(licence)); }catch(e){}
    window.dispatchEvent(new CustomEvent('bozp-licence', {detail: licence}));
  }
  function loadLicence(){
    try{ var c = localStorage.getItem('bozp-licence-' + myOrgId); if(c) licence = JSON.parse(c); }catch(e){}
    Promise.resolve(sb.from('licence').select('stav,pocet,plati_do,uloziste_limit').eq('org_id', myOrgId).maybeSingle()).then(function(res){
      if(!res.error) setLicence(res.data);
    }, function(){});
    loadStorage();
  }
  // Úložiště organizace (portál → audit.licence.uloziste_limit, rozhodnutí uživatele
  // 2026-11-02): využité místo z audit.uloziste_vyuziti(), po nahrání se přičítá.
  var storageUsed = null;
  function loadStorage(){
    Promise.resolve(sb.rpc('uloziste_vyuziti')).then(function(res){
      if(res.error) return;
      storageUsed = Number(res.data) || 0;
      window.dispatchEvent(new CustomEvent('bozp-licence', {detail: licence}));
    }, function(){});
  }
  function storageLimit(){ return licence && licence.uloziste_limit != null ? Number(licence.uloziste_limit) : null; }
  window.__storage = {
    get: function(){ return {used: storageUsed, limit: storageLimit()}; },
    // Vejde se soubor? (bez známého využití nebo limitu = ano, rozhoduje server)
    fits: function(size){ var lim = storageLimit(); return lim == null || storageUsed == null || storageUsed + (size || 0) <= lim; }
  };
  window.__licence = {
    get: function(){ return licence; },
    portalUrl: PORTAL_URL,
    refresh: function(){ if(myOrgId) loadLicence(); }
  };
  document.addEventListener('visibilitychange', function(){ if(!document.hidden && myOrgId) loadLicence(); });
  // Účet z uložené relace Supabase (cookie na bozp-software.cz, jinak localStorage).
  function storedSessionUser(){
    try{
      var store = authStorage() || localStorage;
      var raw = store.getItem(AUTH_KEY);
      if(!raw){ var k = Object.keys(localStorage).find(function(x){ return /^sb-.*-auth-token$/.test(x); }); raw = k && localStorage.getItem(k); }
      var v = raw && JSON.parse(raw);
      return (v && (v.user || (v.currentSession && v.currentSession.user))) || null;
    }catch(e){ return null; }
  }
  function waitForAuth(){
    if(authResolved) return Promise.resolve();
    return new Promise(function(resolve){ authWaiters.push(resolve); });
  }

  // Supabase redirects password-reset / invite links back here with
  // "#...&type=recovery" (or "type=invite") in the URL hash. When that's the
  // case we skip the normal login overlay and wait for the "set new
  // password" overlay instead — the recovery session Supabase hands us isn't
  // something the person chose to log in with, so the app shouldn't boot as
  // them until they've actually set a password.
  var isRecoveryFlow = /type=(recovery|invite)/.test(window.location.hash);

  // Logo na bílé kartě přihlášení / obnovy hesla — z jediného zdroje
  // window.APP_LOGO_SVG (tmavý nápis, bílé dělicí čáry).
  var APP_LOGO_LIGHT_SVG = window.appLogoHtml({text:'#0F172A', gap:'#FFFFFF', height:'44px', style:'margin:0 auto 14px'});
  function showLoginOverlay(){
    if(document.getElementById('sbLoginOverlay') || document.getElementById('sbResetOverlay')) return;
    var overlay = document.createElement('div');
    overlay.id = 'sbLoginOverlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:#11161dcc;display:flex;align-items:center;justify-content:center;z-index:99999;font-family:\'IBM Plex Sans\',system-ui,-apple-system,sans-serif;padding:16px;';
    overlay.innerHTML =
      '<form id="sbLoginForm" style="background:#fff;padding:28px 24px;border-radius:14px;width:320px;max-width:100%;box-shadow:0 10px 40px rgba(0,0,0,.35)">'+
        APP_LOGO_LIGHT_SVG+
        '<div style="font-size:var(--fs-md);color:#5b6673;margin-bottom:16px;text-align:center">Přihlaste se ke svému účtu.</div>'+
        '<input id="sbLoginEmail" type="email" placeholder="E-mail" autocomplete="username" required '+
          'style="width:100%;box-sizing:border-box;padding:11px;margin-bottom:8px;border:1px solid #d7dde3;border-radius:8px;font-size:var(--fs-lg)">'+
        '<input id="sbLoginPassword" type="password" placeholder="Heslo" autocomplete="current-password" required '+
          'style="width:100%;box-sizing:border-box;padding:11px;margin-bottom:6px;border:1px solid #d7dde3;border-radius:8px;font-size:var(--fs-lg)">'+
        '<div style="text-align:right;margin-bottom:14px"><a href="#" id="sbForgotLink" style="font-size:var(--fs-base);color:#1f4e8c;text-decoration:none">Zapomenuté heslo?</a></div>'+
        '<button type="submit" style="width:100%;padding:12px;background:#1f4e8c;color:#fff;border:none;border-radius:8px;font-size:var(--fs-lg);font-weight:600;cursor:pointer">Přihlásit se</button>'+
        '<div id="sbLoginError" style="color:#c22f26;font-size:var(--fs-md);margin-top:10px;min-height:16px"></div>'+
      '</form>';
    document.body.appendChild(overlay);
    prefetchCaptcha(overlay.querySelector('#sbLoginForm')); // ověření Cloudflare na pozadí, ať přihlášení nečeká
    overlay.querySelector('#sbLoginForm').addEventListener('submit', function(e){
      e.preventDefault();
      var email = document.getElementById('sbLoginEmail').value.trim();
      var pass = document.getElementById('sbLoginPassword').value;
      var errEl = document.getElementById('sbLoginError');
      var btn = overlay.querySelector('button[type="submit"]');
      errEl.style.color = '#c22f26';
      errEl.textContent = ''; btn.disabled = true; btn.textContent = 'Přihlašuji…';
      // ochrana proti robotům (Turnstile, core/captcha.js) — úloha se případně ukáže ve formuláři
      captchaToken(errEl.parentNode).then(function(captcha){
        return sb.auth.signInWithPassword({email:email, password:pass, options:{captchaToken:captcha}});
      }, function(e){ return {error:{message:'captcha', captcha:e}}; }).then(function(res){
        btn.disabled = false; btn.textContent = 'Přihlásit se';
        if(res.error){ errEl.textContent = /captcha/i.test(res.error.message||'') ? captchaErrorText(res.error.captcha || {code:'server'}) : 'Nesprávný e-mail nebo heslo.'; return; }
        overlay.remove();
        resolveAuth(res.data.user);
      });
    });
    overlay.querySelector('#sbForgotLink').addEventListener('click', function(e){
      e.preventDefault();
      showForgotPasswordOverlay();
    });
  }

  // ---------- veřejná karta pracoviště (QR kód bez přihlášení) ----------
  // /p/<id> nebo /pracoviste/<id> bez přihlášení: když organizace zapnula
  // „Veřejné informace na QR kódu“ (Pravidla auditů), Edge Function
  // workplace-public vrátí název pracoviště a poslední audit; tlačítko vede na
  // přihlášení, po něm router otevře stránku pracoviště. Jinak rovnou přihlášení.
  function publicWorkplaceIdFromPath(){
    var base = (import.meta.env && import.meta.env.BASE_URL) || '/';
    var p = window.location.pathname;
    if(p.indexOf(base) === 0) p = p.slice(base.length);
    var m = /^(?:p|pracoviste)\/([^\/?#]+)\/?$/.exec(p.replace(/^\/+/, ''));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function escHtml(s){ return String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
  function fmtCzDate(iso){ var d = iso ? new Date(iso) : null; return d && !isNaN(d) ? d.getDate() + '. ' + (d.getMonth() + 1) + '. ' + d.getFullYear() : ''; }
  function showLoginOrPublicCard(){
    var wpId = publicWorkplaceIdFromPath();
    if(!wpId){ showLoginOverlay(); return; }
    withTimeout(Promise.resolve(sb.functions.invoke('workplace-public?id=' + encodeURIComponent(wpId), {method: 'GET'})), 10000).then(function(res){
      var d = res && res.data;
      if(!res || res.error || !d || !d.workplaceName){ showLoginOverlay(); return; }
      showPublicWorkplaceCard(d);
    }, function(){ showLoginOverlay(); });
  }
  function showPublicWorkplaceCard(d){
    if(document.getElementById('sbPublicOverlay')) return;
    var overlay = document.createElement('div');
    overlay.id = 'sbPublicOverlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:#11161dcc;display:flex;align-items:center;justify-content:center;z-index:99999;font-family:\'IBM Plex Sans\',system-ui,-apple-system,sans-serif;padding:16px;';
    var la = d.lastAudit, ZONE = {good:'#10B981', warn:'#F59E0B', bad:'#DC2626'};
    var lastHtml = la ?
      '<div style="font-weight:700;font-size:var(--fs-lg);color:#1a2230">' + escHtml([la.no, fmtCzDate(la.closedAt)].filter(Boolean).join(' · ')) + '</div>' +
      '<div style="color:#5b6673;font-size:var(--fs-md);margin-top:2px">' + escHtml(la.typeName || 'Audit') +
        (la.nokCount ? ' · ' + la.nokCount + ' ' + (la.nokCount === 1 ? 'neshoda' : (la.nokCount < 5 ? 'neshody' : 'neshod')) : '') + '</div>' +
      '<div style="display:flex;gap:10px;align-items:center;justify-content:center;margin-top:10px">' +
        (la.score != null ? '<span id="sbPublicScore" style="font-weight:800;font-size:var(--fs-3xl);color:' + (ZONE[la.scoreZone] || '#1a2230') + '">' + la.score + ' %</span>' : '') +
        (la.overall ? '<span id="sbPublicResult" style="font-weight:700;padding:3px 10px;border-radius:999px;color:#fff;background:' + (la.overall === 'OK' ? '#10B981' : '#DC2626') + '">' + escHtml(la.overall) + '</span>' : '') +
      '</div>' :
      '<div style="color:#5b6673">Na tomto pracovišti zatím nebyl žádný audit.</div>';
    overlay.innerHTML =
      '<div id="sbPublicCard" style="background:#fff;padding:26px 22px;border-radius:14px;width:340px;max-width:100%;box-shadow:0 10px 40px rgba(0,0,0,.35);text-align:center">' +
        APP_LOGO_LIGHT_SVG +
        '<div style="font-weight:700;font-size:var(--fs-2xl);color:#1a2230">' + escHtml(d.workplaceName) + '</div>' +
        '<div style="color:#5b6673;font-size:var(--fs-md);margin-bottom:16px">' + escHtml([d.branchName || 'Sídlo firmy', d.companyName].filter(Boolean).join(' · ')) + '</div>' +
        '<div style="border-top:1px solid #e5e9ee;padding-top:14px;margin-bottom:18px">' +
          '<div style="font-size:var(--fs-sm);font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#94A3B8;margin-bottom:8px">Poslední audit</div>' + lastHtml +
        '</div>' +
        '<button type="button" id="sbPublicLogin" style="width:100%;padding:12px;background:#1f4e8c;color:#fff;border:none;border-radius:8px;font-size:var(--fs-lg);font-weight:600;cursor:pointer">Přihlásit se a spustit audit</button>' +
      '</div>';
    document.body.appendChild(overlay);
    overlay.querySelector('#sbPublicLogin').addEventListener('click', function(){ overlay.remove(); showLoginOverlay(); });
  }

  // Samostatné okno pro "Zapomenuté heslo?" — oddělené od přihlašovacího
  // formuláře, aby šlo zadat e-mail, ke kterému se obnovuje heslo, nezávisle
  // na tom, co je zrovna napsané v poli e-mailu na přihlášení (to pole se dřív
  // bez dalšího dotazu použilo přímo, což bylo matoucí, pokud se uživatel
  // přihlašuje za někoho jiného nebo má v poli překlep).
  function showForgotPasswordOverlay(){
    if(document.getElementById('sbForgotOverlay')) return;
    var prefill = (document.getElementById('sbLoginEmail') && document.getElementById('sbLoginEmail').value.trim()) || '';
    var overlay = document.createElement('div');
    overlay.id = 'sbForgotOverlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:#11161dcc;display:flex;align-items:center;justify-content:center;z-index:100000;font-family:\'IBM Plex Sans\',system-ui,-apple-system,sans-serif;padding:16px;';
    overlay.innerHTML =
      '<form id="sbForgotForm" style="background:#fff;padding:28px 24px;border-radius:14px;width:320px;max-width:100%;box-shadow:0 10px 40px rgba(0,0,0,.35)">'+
        APP_LOGO_LIGHT_SVG+
        '<div style="font-weight:700;font-size:var(--fs-2xl);margin-bottom:4px;color:#1a2230">Zapomenuté heslo</div>'+
        '<div style="font-size:var(--fs-md);color:#5b6673;margin-bottom:16px">Zadejte e-mail, ke kterému chcete obnovit heslo. Pošleme na něj odkaz pro nastavení nového hesla.</div>'+
        '<input id="sbForgotEmail" type="email" placeholder="E-mail" autocomplete="username" required value="'+
          String(prefill).replace(/"/g,'&quot;')+'" '+
          'style="width:100%;box-sizing:border-box;padding:11px;margin-bottom:14px;border:1px solid #d7dde3;border-radius:8px;font-size:var(--fs-lg)">'+
        '<button type="submit" style="width:100%;padding:12px;background:#1f4e8c;color:#fff;border:none;border-radius:8px;font-size:var(--fs-lg);font-weight:600;cursor:pointer">Odeslat odkaz</button>'+
        '<button type="button" id="sbForgotBack" style="width:100%;padding:10px;background:none;color:#5b6673;border:none;font-size:var(--fs-md);margin-top:8px;cursor:pointer">← Zpět</button>'+
        '<div id="sbForgotMsg" style="color:#1f4e8c;font-size:var(--fs-md);margin-top:4px;min-height:16px"></div>'+
      '</form>';
    document.body.appendChild(overlay);
    prefetchCaptcha(overlay.querySelector('#sbForgotForm'));
    overlay.querySelector('#sbForgotBack').addEventListener('click', function(){ overlay.remove(); });
    overlay.querySelector('#sbForgotForm').addEventListener('submit', function(e){
      e.preventDefault();
      var email = document.getElementById('sbForgotEmail').value.trim();
      var msgEl = document.getElementById('sbForgotMsg');
      var btn = overlay.querySelector('button[type="submit"]');
      if(!email){ msgEl.style.color = '#c22f26'; msgEl.textContent = 'Zadejte e-mail.'; return; }
      msgEl.style.color = '#1f4e8c';
      msgEl.textContent = 'Odesílám e-mail…';
      btn.disabled = true;
      captchaToken(msgEl.parentNode).then(function(captcha){
        return sb.auth.resetPasswordForEmail(email, {redirectTo: window.location.origin + ((import.meta.env && import.meta.env.BASE_URL) || '/'), captchaToken: captcha});
      }, function(e){ return {error:{message:'captcha', captcha:e}}; }).then(function(res){
        btn.disabled = false;
        if(res.error){
          msgEl.style.color = '#c22f26';
          msgEl.textContent = /captcha/i.test(res.error.message||'') ? captchaErrorText(res.error.captcha || {code:'server'}) : 'E-mail se nepodařilo odeslat.';
          return;
        }
        msgEl.style.color = '#1f4e8c';
        msgEl.textContent = 'Pokud k tomuto e-mailu existuje účet, přišel na něj odkaz na nastavení nového hesla.';
      });
    });
  }

  function showSetNewPasswordOverlay(){
    if(document.getElementById('sbResetOverlay')) return;
    var loginOverlay = document.getElementById('sbLoginOverlay');
    if(loginOverlay) loginOverlay.remove();
    var overlay = document.createElement('div');
    overlay.id = 'sbResetOverlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:#11161dcc;display:flex;align-items:center;justify-content:center;z-index:99999;font-family:\'IBM Plex Sans\',system-ui,-apple-system,sans-serif;padding:16px;';
    overlay.innerHTML =
      '<form id="sbResetForm" style="background:#fff;padding:28px 24px;border-radius:14px;width:320px;max-width:100%;box-shadow:0 10px 40px rgba(0,0,0,.35)">'+
        APP_LOGO_LIGHT_SVG+
        '<div style="font-weight:700;font-size:var(--fs-2xl);margin-bottom:4px;color:#1a2230">Nastavte si heslo</div>'+
        '<div style="font-size:var(--fs-md);color:#5b6673;margin-bottom:16px">Zadejte nové heslo ke svému účtu.</div>'+
        '<input id="sbResetPassword" type="password" placeholder="Nové heslo (min. 6 znaků)" autocomplete="new-password" required minlength="6" '+
          'style="width:100%;box-sizing:border-box;padding:11px;margin-bottom:8px;border:1px solid #d7dde3;border-radius:8px;font-size:var(--fs-lg)">'+
        '<input id="sbResetPassword2" type="password" placeholder="Zopakujte nové heslo" autocomplete="new-password" required minlength="6" '+
          'style="width:100%;box-sizing:border-box;padding:11px;margin-bottom:14px;border:1px solid #d7dde3;border-radius:8px;font-size:var(--fs-lg)">'+
        '<button type="submit" style="width:100%;padding:12px;background:#1f4e8c;color:#fff;border:none;border-radius:8px;font-size:var(--fs-lg);font-weight:600;cursor:pointer">Uložit heslo</button>'+
        '<div id="sbResetError" style="color:#c22f26;font-size:var(--fs-md);margin-top:10px;min-height:16px"></div>'+
      '</form>';
    document.body.appendChild(overlay);
    overlay.querySelector('#sbResetForm').addEventListener('submit', function(e){
      e.preventDefault();
      var p1 = document.getElementById('sbResetPassword').value;
      var p2 = document.getElementById('sbResetPassword2').value;
      var errEl = document.getElementById('sbResetError');
      var btn = overlay.querySelector('button[type="submit"]');
      errEl.textContent = '';
      if(p1.length < 6){ errEl.textContent = 'Heslo musí mít alespoň 6 znaků.'; return; }
      if(p1 !== p2){ errEl.textContent = 'Hesla se neshodují.'; return; }
      btn.disabled = true; btn.textContent = 'Ukládám…';
      sb.auth.updateUser({password:p1}).then(function(res){
        btn.disabled = false; btn.textContent = 'Uložit heslo';
        if(res.error){ errEl.textContent = 'Heslo se nepodařilo uložit: ' + res.error.message; return; }
        isRecoveryFlow = false;
        try{ history.replaceState(null, '', window.location.pathname + window.location.search); }catch(e){}
        overlay.remove();
        resolveAuth(res.data.user);
      });
    });
  }

  if(isRecoveryFlow){
    // Fallback in case the PASSWORD_RECOVERY event never fires for some reason.
    setTimeout(function(){
      if(!currentUser && !document.getElementById('sbResetOverlay') && !document.getElementById('sbLoginOverlay')) showLoginOverlay();
    }, 4000);
  } else {
    sb.auth.getSession().then(function(res){
      var session = res.data && res.data.session;
      if(session && session.user) resolveAuth(session.user);
      else if(res.error && isNetworkError(res.error) && storedSessionUser()) resolveAuth(storedSessionUser());
      else showLoginOrPublicCard();
    }, function(e){
      // Bez signálu se prošlý token neobnoví — pracovat dál s uloženým účtem
      // (zápisy čekají ve frontě, po návratu signálu se relace obnoví sama).
      if(isNetworkError(e) && storedSessionUser()) resolveAuth(storedSessionUser()); else showLoginOrPublicCard();
    });
  }
  sb.auth.onAuthStateChange(function(event){
    if(event === 'SIGNED_OUT'){ location.reload(); }
    if(event === 'PASSWORD_RECOVERY'){ showSetNewPasswordOverlay(); }
    // Pozvánkové odkazy ("type=invite") nevyvolávají PASSWORD_RECOVERY,
    // ale SIGNED_IN/INITIAL_SESSION — i tak ale uživatel musí nejdřív
    // nastavit heslo, ne rovnou proklouznout do appky nebo vidět
    // přihlašovací okno (viz 4s fallback timeout níže).
    else if(isRecoveryFlow && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && !document.getElementById('sbResetOverlay')){
      showSetNewPasswordOverlay();
    }
  });

  // ---------- db ----------
  function docSnap(row){ return { id: row.id, data: function(){ return row.data || {}; } }; }

  // Each subscribed collection keeps its rows in memory (id -> data) and is
  // kept current row by row: Realtime events carry the changed row itself,
  // and this client's own writes re-read just the one row they touched
  // (Realtime rides on WAL replication and can lag a couple of seconds, so
  // waiting for it made every save feel slow). The whole table is only read
  // on first load and after the Realtime channel (re)connects — e.g. the
  // phone was locked or the signal dropped — so nothing can stay stale.
  // Never refetch every table after a write: each table emits -> render(),
  // and that re-render destroys in-progress input (it closed the native date
  // picker in the audit runner).
  var tableStores = {};
  // PostgREST (Supabase default max-rows) returns at most 1000 rows per
  // request and silently drops the rest — so whole-table reads are paged
  // until a short page comes back.
  var PAGE_SIZE = 1000;
  // filter = {field, value}: jen řádky s data.<field> = value nebo bez něj
  // (starší řádky bez pole patří všem) — načítání auditů jen aktivní firmy.
  function fetchAllRows(table, filter){
    var all = [];
    function page(from){
      var q = sb.from(table).select('id,data');
      if(filter && filter.value) q = q.or('data->>' + filter.field + '.eq.' + filter.value + ',data->>' + filter.field + '.eq.,data->>' + filter.field + '.is.null');
      return q.order('id').range(from, from + PAGE_SIZE - 1).then(function(res){
        if(res.error) return res;
        var rows = res.data || [];
        all = all.concat(rows);
        if(rows.length < PAGE_SIZE) return {data: all, error: null};
        return page(from + PAGE_SIZE);
      });
    }
    return page(0);
  }

  var channelSeq = 0;
  function dbCollection(name){
    var table = tableFor(name);
    var orderField = null, orderDir = 'asc', filter = null;
    // Řádek patří do filtrované kolekce (where): pole chybí, nebo má hodnotu filtru.
    function inFilter(data){
      if(!filter || !filter.value) return true;
      var v = data && data[filter.field];
      return v == null || v === '' || v === filter.value;
    }
    var api = {
      orderBy: function(field, dir){ orderField = field; orderDir = dir || 'asc'; return api; },
      where: function(field, value){ filter = {field: field, value: value}; return api; },
      onSnapshot: function(cb, onErr){
        var stopped = false, channel = null, loadSeq = 0, everSubscribed = false;
        var rows = new Map();   // id -> data
        var loading = true;     // changes arriving mid-load are queued, then replayed
        var pending = [];
        var emitTimer = null;
        function emit(){
          clearTimeout(emitTimer); emitTimer = null;
          if(stopped) return;
          var list = [];
          rows.forEach(function(data, id){ list.push({id: id, data: data}); });
          if(orderField){
            list.sort(function(a,b){
              var av = (a.data||{})[orderField]; if(av==null) av = 0;
              var bv = (b.data||{})[orderField]; if(bv==null) bv = 0;
              if(av < bv) return orderDir === 'desc' ? 1 : -1;
              if(av > bv) return orderDir === 'desc' ? -1 : 1;
              return 0;
            });
          }
          // Nastavení organizace: id v DB „<orgId>:<klíč>“, aplikace vidí jen
          // „<klíč>“; nové řádky mají přednost před staršími bez orgId.
          if(table === 'app_settings'){
            var byKey = {};
            list.forEach(function(d){
              var k = d.id.indexOf(':') >= 0 ? d.id.slice(d.id.indexOf(':') + 1) : d.id;
              if(!byKey[k] || d.id.indexOf(':') >= 0) byKey[k] = {id: k, data: d.data};
            });
            list = Object.keys(byKey).map(function(k){ return byKey[k]; });
          }
          cb({ docs: list.map(docSnap) });
          saveCache();
        }
        // Bursts (a cascade delete, several events at once) -> one render.
        function scheduleEmit(){
          if(!emitTimer) emitTimer = setTimeout(emit, 30);
        }
        function applyChange(fn){
          if(loading){ pending.push(fn); return; }
          fn(); scheduleEmit();
        }
        // Bez signálu: poslední načtená data z IndexedDB (cacheKey) + čekající
        // zápisy z fronty; po návratu signálu se tabulka načte znovu.
        var cacheKey = null, fromCache = false, cacheTimer = null, retryTimer = null;
        function saveCache(){
          clearTimeout(cacheTimer);
          cacheTimer = setTimeout(function(){
            if(stopped || fromCache || !cacheKey) return;
            var list = []; rows.forEach(function(data, id){ list.push([id, data]); });
            idb.put('cache', cacheKey, {rows: list, savedAt: Date.now()});
          }, 1500);
        }
        function overlayPending(m, onlyId){
          if(!currentUser) return;
          outbox.list(currentUser.id).forEach(function(it){
            if(it.table === table && (!onlyId || it.id === onlyId)) applyItem(m, it);
          });
          if(filter && filter.value) m.forEach(function(data, id){ if(!inFilter(data)) m.delete(id); });
        }
        function finishLoad(m){
          overlayPending(m);
          rows = m;
          loading = false;
          var q = pending; pending = [];
          q.forEach(function(fn){ fn(); });
          emit();
        }
        function loadAll(){
          var seq = ++loadSeq;
          loading = true;
          withTimeout(fetchAllRows(table, filter), 30000).then(function(res){
            if(stopped || seq !== loadSeq) return;
            if(res.error && isNetworkError(res.error) && cacheKey){
              return idb.get('cache', cacheKey).then(function(c){
                if(stopped || seq !== loadSeq) return;
                if(!c){ loading = false; pending = []; if(onErr) onErr(res.error); return; }
                fromCache = true;
                finishLoad(new Map(c.rows || []));
                clearTimeout(retryTimer);
                retryTimer = setTimeout(function(){ if(fromCache && !stopped) loadAll(); }, 60000);
              });
            }
            if(res.error){ loading = false; pending = []; if(onErr) onErr(res.error); return; }
            var m = new Map();
            (res.data || []).forEach(function(r){ m.set(r.id, r.data || {}); });
            fromCache = false;
            finishLoad(m);
            saveCache();
          });
        }
        function reloadRow(id){
          sb.from(table).select('id,data').eq('id', id).maybeSingle().then(function(res){
            if(stopped) return;
            if(res.error){ loadAll(); return; }
            applyChange(function(){
              if(res.data && inFilter(res.data.data)) rows.set(id, res.data.data || {});
              else rows.delete(id); // gone, or no longer visible to us (RLS / filter)
              overlayPending(rows, id);
            });
          });
        }
        function removeRow(id){
          applyChange(function(){ rows.delete(id); });
        }
        function onRealtime(payload){
          if(payload.eventType === 'DELETE'){
            var oldId = payload.old && payload.old.id;
            if(oldId) removeRow(oldId); else loadAll();
            return;
          }
          var n = payload.new;
          var incomplete = (payload.errors && payload.errors.length) || !n || n.data === undefined;
          if(n && n.id && !incomplete){
            applyChange(function(){ if(inFilter(n.data)) rows.set(n.id, n.data || {}); else rows.delete(n.id); overlayPending(rows, n.id); });
          } else if(n && n.id){
            reloadRow(n.id); // payload too large / truncated -> read the row itself
          } else {
            loadAll();
          }
        }
        // Zápis z fronty (bez signálu) hned promítnout do dat aplikace.
        function applyLocal(it){
          applyChange(function(){ applyItem(rows, it); if(!inFilter(rows.get(it.id))) rows.delete(it.id); });
        }
        function onOnline(){ if(fromCache && !stopped) loadAll(); }
        window.addEventListener('online', onOnline);
        tableStores[table] = { reloadRow: reloadRow, removeRow: removeRow, applyLocal: applyLocal, resync: onOnline };
        waitForAuth().then(function(){
          if(stopped) return;
          cacheKey = table + '|' + ((filter && filter.value) || '') + '|' + (currentUser ? currentUser.id : '');
          loadAll();
          channel = sb.channel('rt:' + table + (filter && filter.value ? ':' + filter.value : '') + ':' + (++channelSeq))
            .on('postgres_changes', {event:'*', schema:DB_SCHEMA, table:table}, onRealtime)
            .subscribe(function(status){
              if(status !== 'SUBSCRIBED') return;
              // First join: the initial loadAll above covers it. A later
              // SUBSCRIBED is a rejoin after a dropped connection, during
              // which events were missed -> resync the whole table once.
              if(everSubscribed || fromCache) loadAll();
              everSubscribed = true;
            });
        });
        return function(){
          stopped = true; clearTimeout(emitTimer); clearTimeout(cacheTimer); clearTimeout(retryTimer);
          window.removeEventListener('online', onOnline);
          if(tableStores[table] && tableStores[table].reloadRow === reloadRow) delete tableStores[table];
          if(channel) sb.removeChannel(channel);
        };
      },
      // Id vzniká v zařízení, aby šel nový záznam (audit) založit i bez signálu.
      add: function(data){
        var id = newId();
        return waitForAuth().then(function(){
          return writeOp({table: table, id: id, op: 'set', data: Object.assign({}, data, {orgId: myOrgId})});
        }).then(function(){ return {id: id}; });
      }
    };
    return api;
  }

  // After this client's own write: re-read just that row (or drop it).
  function reloadStoredRow(table, id){
    if(tableStores[table]) tableStores[table].reloadRow(id);
  }
  function dropStoredRow(table, id){
    if(tableStores[table]) tableStores[table].removeRow(id);
  }

  function dbDoc(fullPath){
    var idx = fullPath.indexOf('/');
    var name = fullPath.slice(0, idx), docId = fullPath.slice(idx + 1);
    var table = tableFor(name);
    var id = docId;
    // Nastavení organizace (app_settings) má v DB id „<orgId>:<klíč>“ —
    // dřív jen „<klíč>“ a druhá organizace by ho nemohla uložit (2026-10-31).
    function resolveId(){ if(table === 'app_settings' && docId.indexOf(':') < 0) id = myOrgId + ':' + docId; }
    return {
      set: function(data){
        return waitForAuth().then(function(){
          resolveId();
          return writeOp({table: table, id: id, op: 'set', data: Object.assign({}, data, {orgId: myOrgId})});
        });
      },
      // Merged server-side in one statement (public.merge_doc: shallow
      // data || patch, RLS still applies) so concurrent edits of different
      // fields of the same row don't overwrite each other. Keys set to
      // undefined are removed, same as the old read-merge-write did.
      update: function(partial){
        return waitForAuth().then(function(){
          resolveId();
          var patch = {}, remove = [];
          Object.keys(partial || {}).forEach(function(k){
            if(partial[k] === undefined) remove.push(k); else patch[k] = partial[k];
          });
          patch.orgId = myOrgId;
          return writeOp({table: table, id: id, op: 'merge', patch: patch, remove: remove});
        });
      },
      delete: function(){
        return waitForAuth().then(function(){
          resolveId();
          return writeOp({table: table, id: id, op: 'delete'});
        });
      }
    };
  }

  // ---------- zápisy a fronta bez signálu ----------
  // Každý zápis jde přes writeOp: s připojením rovnou na server; bez signálu
  // (nebo když ve frontě už něco čeká — kvůli pořadí) do fronty v zařízení
  // (offline.js), změna se hned promítne do dat aplikace a odešle se, až bude
  // signál. Server zápis zamítne (oprávnění, data) → zahodí se a aplikace
  // ukáže hlášku (událost bozp-sync-error).
  var WRITE_TIMEOUT = 20000;
  function newId(){
    if(window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'x' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }
  function execOp(it){
    var q;
    if(it.op === 'merge') q = sb.rpc('merge_doc', {p_table: it.table, p_id: it.id, p_patch: it.patch, p_remove: it.remove});
    else if(it.op === 'set') q = sb.from(it.table).upsert({id: it.id, data: it.data});
    else q = sb.from(it.table).delete().eq('id', it.id);
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    if(ctl && q && typeof q.abortSignal === 'function') q = q.abortSignal(ctl.signal);
    return withTimeout(Promise.resolve(q), WRITE_TIMEOUT, ctl && function(){ ctl.abort(); });
  }
  function afterWrite(it){
    if(it.op === 'delete') dropStoredRow(it.table, it.id); else reloadStoredRow(it.table, it.id);
  }
  function queueOffline(it){
    if(!outbox.add(it)) return false;
    if(tableStores[it.table] && tableStores[it.table].applyLocal) tableStores[it.table].applyLocal(it);
    notifySync();
    scheduleFlush(3000);
    return true;
  }
  // Bez platné licence jen ke čtení — kromě osobního nastavení, podnětů
  // a správy uživatelů (pozastavení nadbytečných). Rozhoduje i databáze.
  var READ_ONLY_ALLOWED = {profiles: 1, feedback: 1, roles: 1};
  function writeOp(it){
    if(window.__licenceReadOnly && window.__licenceReadOnly() && !READ_ONLY_ALLOWED[it.table]){
      return Promise.reject({message: 'Aplikace je jen ke čtení — předplatné není aktivní.', code: 'read_only', table: it.table, id: it.id, op: it.op});
    }
    it.uid = currentUser ? currentUser.id : '';
    if(isOffline() || outbox.count(it.uid)){
      if(queueOffline(it)) return Promise.resolve({});
      return Promise.reject({message: 'Úložiště zařízení je plné.', code: 'offline_full'});
    }
    return execOp(it).then(function(res){
      if(res.error){
        if(isNetworkError(res.error) && queueOffline(it)) return {};
        throw res.error;
      }
      afterWrite(it);
      return {};
    });
  }
  var flushing = false, flushTimer = null;
  function scheduleFlush(ms){
    clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, ms == null ? 0 : ms);
  }
  // Odeslat frontu: nejdřív fotky, pak zápisy v pořadí. Chyba sítě = konec
  // a nový pokus za 30 s (nebo hned po události online).
  function flush(){
    if(flushing || !currentUser || !authResolved) return;
    if(isOffline()){ notifySync(); return; }
    flushing = true;
    var uid = currentUser.id;
    flushPhotos(uid).then(function(ok){
      if(!ok) return false;
      return (function next(){
        var it = outbox.list(uid)[0];
        if(!it) return true;
        outbox.setSending(it.seq);
        return execOp(it).then(function(res){
          outbox.setSending(null);
          if(res.error && isNetworkError(res.error)) return false;
          // Token prošel během výpadku → obnovit relaci a zkusit znovu (nezahazovat).
          if(res.error && (/JWT|token/i.test(String(res.error.message||'')) || /^PGRST30/.test(String(res.error.code||'')))){
            sb.auth.refreshSession().catch(function(){});
            return false;
          }
          outbox.remove(it.seq);
          if(res.error){
            console.error('offline write rejected', it.table, it.id, res.error);
            window.dispatchEvent(new CustomEvent('bozp-sync-error', {detail: {code: res.error.code || '', table: it.table}}));
            if(tableStores[it.table]) tableStores[it.table].reloadRow(it.id);
          } else afterWrite(it);
          notifySync();
          return next();
        });
      })();
    }).then(function(done){
      flushing = false;
      notifySync();
      if(!done || outbox.count(uid) || pendingPhotoCount) scheduleFlush(30000);
    }, function(){ flushing = false; scheduleFlush(30000); });
  }

  // ---------- fotky bez signálu ----------
  // Nenahraná fotka se uloží do IndexedDB (photos, klíč = cesta v úložišti)
  // a audit dostane její budoucí adresu. Dokud se nenahraje, obrázky s touto
  // adresou ukazují soubor ze zařízení (photoUrls + MutationObserver).
  var photoUrls = {}, pendingPhotoCount = 0;
  function publicUrlOf(path){ return sb.storage.from('audit-photos').getPublicUrl(path).data.publicUrl; }
  function swapImg(img){
    var src = img.getAttribute('src') || '';
    if(!src || src.indexOf('blob:') === 0) return;
    var local = photoUrls[src.split('?')[0]];
    if(local) img.setAttribute('src', local);
  }
  function swapAll(root){
    if(!pendingPhotoCount || !root) return;
    if(root.tagName === 'IMG') swapImg(root);
    if(root.querySelectorAll) root.querySelectorAll('img').forEach(swapImg);
  }
  if(typeof MutationObserver === 'function'){
    new MutationObserver(function(muts){
      if(!pendingPhotoCount) return;
      muts.forEach(function(m){
        if(m.type === 'attributes') swapImg(m.target);
        else m.addedNodes.forEach(function(n){ if(n.nodeType === 1) swapAll(n); });
      });
    }).observe(document.documentElement, {childList: true, subtree: true, attributes: true, attributeFilter: ['src']});
  }
  function rememberPhoto(path, blob){
    var url = publicUrlOf(path);
    if(!photoUrls[url]){ photoUrls[url] = URL.createObjectURL(blob); pendingPhotoCount++; }
  }
  function forgetPhoto(path){
    var url = publicUrlOf(path);
    if(photoUrls[url]){ var local = photoUrls[url]; delete photoUrls[url]; pendingPhotoCount--; setTimeout(function(){ URL.revokeObjectURL(local); }, 60000); }
  }
  function loadPendingPhotos(uid){
    return idb.all('photos').then(function(list){
      list.forEach(function(e){ if(e.value && e.value.uid === uid && !photoUrls[publicUrlOf(e.key)]) rememberPhoto(e.key, e.value.blob); });
      notifySync();
      if(list.length) swapAll(document.body);
    });
  }
  function flushPhotos(uid){
    return idb.all('photos').then(function(list){
      list = list.filter(function(e){ return e.value && e.value.uid === uid; });
      return (function next(i){
        if(i >= list.length) return true;
        var e = list[i];
        return withTimeout(Promise.resolve(sb.storage.from('audit-photos').upload(e.key, e.value.blob, {contentType: e.value.type, upsert: true})), 60000).then(function(res){
          if(res.error && isNetworkError(res.error)) return false;
          if(res.error) console.error('offline photo rejected', e.key, res.error);
          return idb.del('photos', e.key).then(function(){ forgetPhoto(e.key); notifySync(); return next(i + 1); });
        });
      })(0);
    });
  }

  // ---------- stav synchronizace (štítek „Bez signálu“) ----------
  // window.__sync.state() → {offline, pending}; aplikace poslouchá událost bozp-sync.
  function notifySync(){
    window.dispatchEvent(new CustomEvent('bozp-sync', {detail: syncState()}));
  }
  function syncState(){
    return {offline: isOffline(), pending: (currentUser ? outbox.count(currentUser.id) : 0) + pendingPhotoCount};
  }
  window.__sync = { state: syncState, flush: function(){ scheduleFlush(0); },
    clearCache: function(){ return idb.clear('cache'); } };
  var syncStarted = false;
  function startSync(){
    if(syncStarted || !currentUser) return;
    syncStarted = true;
    window.addEventListener('online', function(){ notifySync(); scheduleFlush(500); });
    window.addEventListener('offline', notifySync);
    loadPendingPhotos(currentUser.id).then(function(){ scheduleFlush(1000); });
  }

  var dbCap = { collection: dbCollection, doc: dbDoc };
  // Jednorázové čtení mimo načtená data (audity se drží jen pro aktivní firmu):
  // audity firmy (mazání firmy), audit podle id nebo čísla (odkazy na jinou firmu).
  dbCap.fetchWhere = function(name, field, value){
    return waitForAuth().then(function(){
      return fetchAllRows(tableFor(name), {field: field, value: value});
    }).then(function(res){ return (res.data || []).filter(function(r){ return (r.data||{})[field] === value; }); });
  };
  // Firmy auditu podle id nebo čísla („BOZP-12“, „12“) — nejdřív id, pak číslo.
  // Vrací pole companyId (prázdné = nenalezeno); výběr firmy s přístupem dělá router.
  dbCap.findAuditCompany = function(ref){
    ref = String(ref || '').trim();
    function first(res){
      var rows = ((res && res.data) || []).filter(function(r){ var d = r.data || {}; return !d.deletedAt; });
      return rows.length ? rows.map(function(r){ return (r.data || {}).companyId || ''; }) : null;
    }
    return waitForAuth().then(function(){
      return sb.from('audits').select('id,data').eq('id', ref).limit(1);
    }).then(function(res){
      var hit = first(res);
      if(hit !== null) return hit;
      var m = /^(?:(.+)-)?(\d+)$/.exec(ref.replace(/^#/, ''));
      if(!m) return [];
      var q = sb.from('audits').select('id,data').eq('data->>auditNumber', String(parseInt(m[2], 10)));
      if(m[1]) q = q.ilike('data->>auditCode', m[1]);
      return q.limit(20).then(function(r){ return first(r) || []; });
    });
  };


  // ---------- user ----------
  var userCap = {
    me: function(){
      return waitForAuth().then(function(){
        return sb.from('profiles').select('data').eq('id', currentUser.id).maybeSingle();
      }).then(function(res){
        var p = (res.data && res.data.data) || {};
        // The profile doc stores the display name under "displayName" (see
        // the profile written by the portal (audit.nastavit_clena/nastavit_profil)
        // in the other module) — this used to read "name" instead, a field
        // that's never written, so it silently always fell back to the
        // email. That's why things like the opatření status-change history
        // showed the email instead of the entered jméno.
        return { id: currentUser.id, name: p.displayName || currentUser.email || '', email: currentUser.email || '', isOwner:false, canEdit:false };
      });
    },
    can: function(){ return Promise.resolve(true); },
    profiles: function(ids){
      return waitForAuth().then(function(){
        return sb.from('profiles').select('id,data').in('id', ids || []);
      }).then(function(res){
        var out = {};
        (res.data || []).forEach(function(r){
          var d = r.data || {};
          // displayName is where the portal writes the name (with titles)
          // (see userCap.me's comment above) — "name" is a legacy field that
          // nothing in the app writes anymore, kept here only as a fallback
          // for any very old row that might still have it.
          out[r.id] = { name: d.displayName || d.name || '', email: d.email || '', department: d.department || '', phone: d.phone || '' };
        });
        return out;
      });
    },
    search: function(q){
      return waitForAuth().then(function(){
        return sb.from('profiles').select('id,data').ilike('data->>displayName', '%' + (q||'') + '%').limit(20);
      }).then(function(res){
        return (res.data || []).map(function(r){ var d = r.data || {}; return {id:r.id, name:d.displayName || d.name || '', email:d.email || ''}; });
      });
    }
  };

  // ---------- assets (fotky) ----------
  var assetsCap = {
    upload: function(file){
      // Prefixed with the org folder so Storage RLS can keep one org's
      // photos from being listed/read/deleted by another org's members.
      // Bez signálu se fotka uloží do zařízení a nahraje se později (flushPhotos).
      var path;
      function keepLocal(){
        return idb.put('photos', path, {blob: file, type: file.type, uid: currentUser.id, savedAt: Date.now()}).then(function(){
          return idb.get('photos', path);
        }).then(function(saved){
          if(!saved) throw {message: 'Fotku se nepodařilo uložit do zařízení.'};
          rememberPhoto(path, file);
          notifySync();
          scheduleFlush(3000);
          return { id: path, url: publicUrlOf(path), contentType: file.type };
        });
      }
      if(!window.__storage.fits(file && file.size)){
        return Promise.reject({code: 'storage_full', message: 'Úložiště organizace je plné — fotku ani přílohu nejde nahrát. Administrátor může přikoupit místo v portálu, nebo smazat staré audity a přílohy.'});
      }
      return waitForAuth().then(function(){
        // Název v cestě jen bez diakritiky a zvláštních znaků (úložiště je nepřijme);
        // původní název souboru si ukládá volající (příloha opatření → name).
        var safeName = String(file.name || 'photo').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9._-]+/g, '_').slice(-80) || 'soubor';
        path = myOrgId + '/' + Date.now() + '_' + Math.random().toString(36).slice(2) + '_' + safeName;
        if(isOffline()) return keepLocal();
        return withTimeout(Promise.resolve(sb.storage.from('audit-photos').upload(path, file, {contentType: file.type})), 60000).then(function(res){
          if(res.error && isNetworkError(res.error)) return keepLocal();
          if(res.error) throw res.error;
          if(storageUsed != null){ storageUsed += file.size || 0; window.dispatchEvent(new CustomEvent('bozp-licence', {detail: licence})); }
          return { id: path, url: publicUrlOf(path), contentType: file.type };
        });
      });
    },
    delete: function(id){
      if(photoUrls[publicUrlOf(id)]){ forgetPhoto(id); notifySync(); return idb.del('photos', id).then(function(){ return {}; }); }
      return waitForAuth().then(function(){
        return sb.storage.from('audit-photos').remove([id]);
      }).then(function(){ return {}; });
    },
    // Batch form of delete() — used when cascading a delete (a whole audit,
    // workplace, branch or company) so every photo it owns is removed from
    // Storage in one request instead of one-by-one.
    deleteMany: function(ids){
      if(!ids || !ids.length) return Promise.resolve({});
      return waitForAuth().then(function(){
        return sb.storage.from('audit-photos').remove(ids);
      }).then(function(){ return {}; });
    }
  };

  // ---------- downloads ----------
  // Na telefonu/tabletu, který umí sdílet soubory (iPhone), se místo
  // stažení ukáže list "Soubor je připravený" s tlačítky Sdílet / Stáhnout.
  // Sdílení musí spustit klepnutí (iOS nedovolí navigator.share po
  // několikasekundovém generování PDF), proto mezikrok s tlačítkem.
  function downloadBlob(blob, filename){
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = filename || 'stazeni';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(function(){ URL.revokeObjectURL(url); }, 2000);
  }
  var pendingShareFile = null;
  function showShareSheet(file){
    pendingShareFile = file;
    var root = document.getElementById('overlayRoot');
    if(!root) return false;
    root.innerHTML = '<div class="overlay company-switch-overlay" onclick="App.closeShareSheet(event)">' +
      '<div class="sheet share-sheet" onclick="event.stopPropagation()">' +
      '<div class="sheet-head"><h3>Soubor je připravený</h3><button class="icon-btn" onclick="App.closeShareSheet()">×</button></div>' +
      '<p id="shareSheetName"></p>' +
      '<button class="btn btn-primary btn-block" onclick="App.shareReadyFile()">Sdílet…</button>' +
      '<button class="btn btn-block" onclick="App.downloadReadyFile()">Stáhnout</button>' +
      '</div></div>';
    document.getElementById('shareSheetName').textContent = file.name; // (esc() je až v dalším skriptu)
    return true;
  }
  window.App = window.App || {};
  App.closeShareSheet = function(e){
    if(e && e.target && e.target.closest && e.target.closest('.sheet')) return;
    var root = document.getElementById('overlayRoot'); if(root) root.innerHTML = '';
    pendingShareFile = null;
  };
  App.shareReadyFile = function(){
    var f = pendingShareFile; if(!f) return;
    navigator.share({files:[f], title:f.name}).catch(function(){});
    App.closeShareSheet();
  };
  App.downloadReadyFile = function(){
    var f = pendingShareFile; if(!f) return;
    downloadBlob(f, f.name);
    App.closeShareSheet();
  };
  var downloadsCap = {
    save: function(opts){
      try{
        var blob = (opts.data instanceof Blob) ? opts.data : new Blob([opts.data], {type:'application/octet-stream'});
        var touch = !(window.matchMedia && window.matchMedia('(hover:hover) and (pointer:fine)').matches);
        if(touch && typeof File==='function' && navigator.share && navigator.canShare){
          var file = new File([blob], opts.filename || 'soubor', {type: blob.type || 'application/octet-stream'});
          if(navigator.canShare({files:[file]}) && showShareSheet(file)) return Promise.resolve({status:'shown'});
        }
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url; a.download = opts.filename || 'stazeni';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(function(){ URL.revokeObjectURL(url); }, 2000);
        return Promise.resolve({status:'saved'});
      }catch(e){ return Promise.resolve({status:'error'}); }
    }
  };

  // supabase-js only gives a generic "non-2xx status code" message on
  // res.error by default when calling an edge function — the function's own
  // JSON error body (the actually useful message) has to be read separately
  // from the raw response it attaches as res.error.context.
  function unwrapFunctionError(res){
    if(res && res.error && res.error.context && typeof res.error.context.json === 'function'){
      return res.error.context.json().then(function(body){
        var msg = (body && body.error) || res.error.message;
        return {data:res.data, error:{message:msg}};
      }).catch(function(){ return res; });
    }
    return res;
  }

  window.__sbAuth = {
    // scope:'local' only clears the session on this device and never waits
    // on a network round-trip to Supabase — a 'global' sign-out (the
    // default) also asks the server to revoke the refresh token, which on a
    // flaky mobile connection can hang the whole logout indefinitely.
    signOut: function(){ return sb.auth.signOut({scope:'local'}); },
    // Pojistka odhlášení: signOut() čeká na zámek relace a může viset
    // (souběžné obnovení tokenu, slabý signál) — pak by se stránka obnovila
    // s pořád uloženým přihlášením. Smaže uloženou relaci Supabase přímo.
    clearLocalSession: function(){
      try{
        Object.keys(localStorage).forEach(function(k){
          if(/^sb-.*-auth-token(-code-verifier)?$/.test(k)) localStorage.removeItem(k);
        });
        cookieStorage.removeItem(AUTH_KEY);
        cookieStorage.removeItem(AUTH_KEY + '-code-verifier');
      }catch(e){}
    },
    // Změna hesla jen odkazem e-mailem (rozhodnutí uživatele 2026-11-02) — heslo se
    // nastaví na stránce portálu /nove-heslo/ (musí být v Supabase Auth → Redirect URLs).
    sendPasswordReset: function(redirectTo, captchaContainer){
      return waitForAuth().then(function(){
        if(!currentUser || !currentUser.email) return {error:{message:'Chybí e-mail přihlášeného účtu.'}};
        return captchaToken(captchaContainer).then(function(captcha){
          return sb.auth.resetPasswordForEmail(currentUser.email, {redirectTo: redirectTo, captchaToken: captcha});
        }, function(e){ return {error:{message:captchaErrorText(e)}}; });
      }).then(null, function(e){ return {error:{message:(e && e.message) || String(e)}}; });
    },
    // Oznámení zástupci e-mailem hned po předání termínu (Edge Function
    // notify-delegation ověří, že předání zadal přihlášený auditor).
    // Podněty: e-mail autorovi o vyřízení / zpráva provozovateli (Edge Function feedback-mail).
    feedbackMail: function(body){
      return waitForAuth().then(function(){
        return sb.functions.invoke('feedback-mail', { body: body });
      }).then(unwrapFunctionError);
    },
    notifyDelegation: function(id){
      return waitForAuth().then(function(){
        return sb.functions.invoke('notify-delegation', { body: { id: id } });
      }).then(unwrapFunctionError);
    },
    createUser: function(email, role, access, names){
      access = access || {}; names = names || {};
      return waitForAuth().then(function(){
        return sb.functions.invoke('admin-create-user', {
          body: {
            email: email,
            role: role,
            firstName: names.firstName || '',
            lastName: names.lastName || '',
            companyIds: access.companyIds || [],
            allCompanies: access.allCompanies === true,
            redirectTo: window.location.origin + ((import.meta.env && import.meta.env.BASE_URL) || '/')
          }
        });
      }).then(unwrapFunctionError);
    },
    deleteUser: function(id){
      return waitForAuth().then(function(){
        return sb.functions.invoke('admin-delete-user', { body: { id: id } });
      }).then(unwrapFunctionError);
    }
  };

  window.claude = {
    use: function(name){
      if(name === 'db') return Promise.resolve(dbCap);
      if(name === 'user') return Promise.resolve(userCap);
      if(name === 'assets') return Promise.resolve(assetsCap);
      if(name === 'downloads') return Promise.resolve(downloadsCap);
      return Promise.resolve(null);
    }
  };
})();
